/**
 * The bottom sheet.
 *
 * Sheets, not dialogs. A modal dialog centred over the graph would cover the one thing the
 * player is looking at while their hands are still on the keys; a sheet rising from the
 * bottom of an iPad in landscape covers the part of the screen nearest the music desk and
 * furthest from the graph. Dismissal is a tap anywhere outside, Escape, or the handle —
 * three ways out, because getting *out* of a sheet must never require aim.
 *
 * Deliberately not a `<dialog>`: `showModal()` traps focus and makes the graph inert, and
 * the graph must keep updating behind an open sheet. The player has not stopped playing
 * just because they tapped something.
 *
 * ## On a desktop window it is a side panel
 *
 * The reasoning above is about an iPad in landscape, where the bottom of the screen is the
 * edge nearest the music desk. On a browser window wider than any iPad it does not hold: a
 * 42rem sheet rising into the bottom of a 1700-point window covers the branch column,
 * leaves two thirds of the glass empty, and makes the app feel like a phone that has been
 * stretched. Past `WIDE_LAYOUT_PX` the same sheet is a full-height panel down the right
 * edge — the shape lands in `tokens.css`, and the only thing this component has to know is
 * which way it comes in from, because a panel that slid up from below would be a bottom
 * sheet wearing a side panel's clothes.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useWideLayout } from './AppFrame.js';
import { useCalmMotion } from './motionPreference.js';
import { CALM_FADE } from '../graph/motion.js';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Rendered small above the title — what this sheet is about. */
  eyebrow?: ReactNode;
  children: ReactNode;
  testId?: string;
}

export function BottomSheet({
  open,
  onClose,
  title,
  eyebrow,
  children,
  testId = 'hc-sheet',
}: BottomSheetProps): React.JSX.Element {
  const calm = useCalmMotion();
  const wide = useWideLayout();
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** The edge the panel arrives from: the bottom on an iPad, the right on a desktop. */
  const offscreen = wide ? { x: '100%' } : { y: '100%' };
  const seated = wide ? { x: 0 } : { y: 0 };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Move focus in so the sheet is reachable from a keyboard, without trapping it.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (panel && typeof panel.focus === 'function') panel.focus();
  }, [open]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="hc-sheet-layer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={CALM_FADE}
        >
          <button
            type="button"
            className="hc-sheet-scrim"
            aria-label="Close"
            onClick={onClose}
            tabIndex={-1}
          />
          <motion.div
            ref={panelRef}
            className="hc-sheet"
            data-testid={testId}
            data-wide={wide ? 'true' : undefined}
            role="group"
            aria-label={title}
            tabIndex={-1}
            initial={calm ? { opacity: 0 } : offscreen}
            animate={calm ? { opacity: 1 } : seated}
            exit={calm ? { opacity: 0 } : offscreen}
            transition={calm ? CALM_FADE : { type: 'spring', stiffness: 380, damping: 40 }}
          >
            <button
              type="button"
              className="hc-sheet__handle"
              onClick={onClose}
              aria-label={`Close ${title}`}
            />
            <header className="hc-sheet__header">
              {eyebrow ? <p className="hc-sheet__eyebrow">{eyebrow}</p> : null}
              <h2 className="hc-sheet__title">{title}</h2>
            </header>
            <div className="hc-sheet__body">{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
